import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AliasCrudTable,
  AliasItem,
} from 'src/components/common/AliasCrudTable';
import {
  useAliases,
  useUpsertAliases,
  useDeleteAlias,
} from 'src/api/core/aliases/useAliases';
import { ProductGroup } from 'src/api/rebates/useProductGroups';

// Separator used in storage format
const STORAGE_SEPARATOR = '|||';
// Display separator (dot with spaces)
const DISPLAY_SEPARATOR = ' . ';

interface ProductGroupAliasesModalProps {
  open: boolean;
  onClose: () => void;
  productGroup: ProductGroup | null;
}

export function ProductGroupAliasesModal({
  open,
  onClose,
  productGroup,
}: ProductGroupAliasesModalProps) {
  const {
    data: aliasData,
    isLoading,
    refetch,
  } = useAliases(
    'product_group',
    productGroup?.id || '',
    open && !!productGroup,
  );

  const { mutateAsync: upsertAliases } = useUpsertAliases();
  const { mutateAsync: deleteAliasRequest } = useDeleteAlias();

  // Transform aliases data for AliasCrudTable
  // The API now returns addedByUserName directly
  const aliases: AliasItem[] = useMemo(() => {
    if (!aliasData?.aliases) return [];
    return aliasData.aliases.map((a) => ({
      id: a.id,
      alias: a.alias,
      addedByUserId: a.addedByUserId,
      addedByUserName: a.addedByUserName,
      createdAt: a.createdAt,
    }));
  }, [aliasData]);

  // Format alias for display: '|||' → ' . '
  const formatAlias = (alias: string): string => {
    return alias.replaceAll(STORAGE_SEPARATOR, DISPLAY_SEPARATOR);
  };

  // Parse user input back to storage format: ' . ' → '|||'
  // Also handles variations like '. ' or ' .' or just '.'
  const parseAlias = (userInput: string): string => {
    return userInput
      .replace(/\s*\.\s*/g, STORAGE_SEPARATOR)
      .replace(/^(\|\|\|)+/, '') // Remove leading separators
      .replace(/(\|\|\|)+$/, ''); // Remove trailing separators
  };

  const handleCreateAlias = async (alias: string) => {
    if (!productGroup) return;
    await upsertAliases({
      entityType: 'product_group',
      entityId: productGroup.id,
      aliases: [alias],
    });
    refetch();
  };

  const handleUpdateAlias = async (
    _id: string,
    newAlias: string,
    oldAlias: string,
  ) => {
    if (!productGroup) return;
    // Delete the old alias and create the new one
    await deleteAliasRequest({
      entityType: 'product_group',
      entityId: productGroup.id,
      alias: oldAlias,
    });
    await upsertAliases({
      entityType: 'product_group',
      entityId: productGroup.id,
      aliases: [newAlias],
    });
    refetch();
  };

  const handleDeleteAlias = async (aliasItem: AliasItem) => {
    if (!productGroup) return;
    await deleteAliasRequest({
      entityType: 'product_group',
      entityId: productGroup.id,
      alias: aliasItem.alias,
    });
    refetch();
  };

  if (!productGroup) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aliases for {productGroup.name}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <AliasCrudTable
            title="Product Group Aliases"
            description="Alternative names that map to this product group during data processing. Use dots (.) to separate parts."
            aliases={aliases}
            isLoading={isLoading}
            onCreateAlias={handleCreateAlias}
            onUpdateAlias={handleUpdateAlias}
            onDeleteAlias={handleDeleteAlias}
            emptyMessage="No aliases found for this product group"
            aliasLabel="Alias"
            formatAlias={formatAlias}
            parseAlias={parseAlias}
            compact
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ProductGroupAliasesModal;
